import {
  contactsSearchRequest,
  getNavigationSearchResults,
  stakeholdersSearchRequest
} from './api-calls-search'
import type { SearchResult } from './search-types'
import { type UserService } from '@gat-ship/lib/user'
import type { IconValues } from '@gat-ship/icons'
import { ref, type Ref, type ComputedRef, computed, watch } from 'vue'
import { type SearchFieldModelValue } from '../components/search-filter-input/search-filter-input.vue'
import type { StakeholderType, GatshipSearchOperator } from '@gat-ship/types'

export function defineFilter(filter: {
  fieldName: SearchFieldModelValue['fieldName']
  defaultOperator: GatshipSearchOperator
  defaultFilterValue?: string
  allowedOperators?: Partial<Record<GatshipSearchOperator, boolean>>
}): {
  filterValue: Ref<string | undefined>
  operator: Ref<GatshipSearchOperator>
  fieldName: SearchFieldModelValue['fieldName']
  allowedOperators: ComputedRef<Partial<Record<GatshipSearchOperator, boolean>>>
} {
  const filterValue = ref<string | undefined>(filter.defaultFilterValue ?? '')

  const operator = ref<GatshipSearchOperator>(filter.defaultOperator)

  const allowedOperators = computed(() => {
    return (
      filter.allowedOperators ?? {
        [filter.defaultOperator]: true
      }
    )
  })

  return {
    filterValue,
    operator,
    fieldName: filter.fieldName,
    allowedOperators
  }
}

export function defineSearchFilters<
  T extends Record<string, ReturnType<typeof defineFilter>>
>(filters: T): T {
  return filters
}
/** A definition of a filter for a search context. */
export type SearchContextFilterDefinition<T extends string> = {
  [key in T]: ReturnType<typeof defineSearchFilters>
}

/** An object of search contexts. */
export const searchContextItems = {
  global: 'global',
  client: 'client',
  vendors: 'vendors',
  otherParties: 'otherParties',
  contacts: 'contacts',
  navigation: 'navigation'
} as const

export type SearchContextMetadata = {
  title: string
  description: string
  prefix: string
  icon: IconValues
}

export type SearchContextType =
  (typeof searchContextItems)[keyof typeof searchContextItems]

export const searchContextTypesMapping: Record<
  SearchContextType,
  SearchContextMetadata
> = {
  global: {
    title: 'All',
    description: 'Search in all contexts',
    prefix: 'G',
    icon: 'Search'
  },
  client: {
    title: 'Clients',
    description: 'Search for clients',
    prefix: 'C',
    icon: 'User'
  },
  vendors: {
    title: 'Vendors',
    description: 'Search for vendors',
    prefix: 'V',
    icon: 'User'
  },
  otherParties: {
    title: 'Other Parties',
    description: 'Search for other parties',
    prefix: 'OP',
    icon: 'User'
  },
  contacts: {
    title: 'Contacts',
    description: 'Search for contacts',
    prefix: 'CT',
    icon: 'User'
  },
  navigation: {
    title: 'Navigation',
    description: 'Search for navigation',
    prefix: 'N',
    icon: 'Link'
  }
} as const

function constructStakeholderSearchQuery(
  search: string | undefined,
  stakeholderType: StakeholderType
): string {
  return `type:"${stakeholderType}" ${search ?? ''}`
}

/* A map of search functions for each context. The search function is used to search for results in the context. */
const searchFunctions: Record<
  SearchContextType,
  (search: string | undefined, user: UserService) => Promise<SearchResult[]>
> = {
  client: (search: string | undefined, user: UserService) =>
    stakeholdersSearchRequest({
      query: constructStakeholderSearchQuery(search, 'client'),
      queryMode: 'free-text'
    }),
  vendors: (search: string | undefined, user: UserService) =>
    stakeholdersSearchRequest({
      query: constructStakeholderSearchQuery(search, 'vendor'),
      queryMode: 'free-text'
    }),
  otherParties: (search: string | undefined, user: UserService) =>
    stakeholdersSearchRequest({
      query: constructStakeholderSearchQuery(search, 'other'),
      queryMode: 'free-text'
    }),
  contacts: (search: string | undefined, user: UserService) =>
    contactsSearchRequest({
      query: search ?? '',
      queryMode: 'free-text',
      includeTotalCount: false,
      pageNumber: 1,
      pageSize: 10,
      totalLimit: 10
    }),
  navigation: (search: string | undefined, user: UserService) =>
    getNavigationSearchResults(search, user),
  global: async (search: string | undefined, user: UserService) => {
    const results = await Promise.all([
      getNavigationSearchResults(search, user),
      stakeholdersSearchRequest({
        query: search ?? '',
        queryMode: 'free-text'
      })
      /* contactsSearchRequest({
        query: search ?? '',
        queryMode: 'free-text'
      }) */
    ])
    return results.flat()
  }
}

export type SearchContextFilterDefinitions = Record<
  SearchContextType,
  ReturnType<typeof defineSearchFilters>
>

export async function globalSearchFn(
  search: string,
  context: SearchContextType,
  user: UserService
) {
  const searchFunction = searchFunctions[context]
  return await searchFunction(search, user)
}
